import React, { useEffect } from 'react'
import styles from './privacyAndConsents.module.scss'
import AccountSection from './AccountSection'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import InfoBox from 'components/generic/InfoBox'
import { getOwnProfile, updateOwnProfile } from 'actions/profile-actions'
import BtnPrimary from 'components/generic/BtnPrimary'
import { Form, Formik } from 'formik'
import { FormikNames } from 'types/forms/formik-names'
import FormInputGroup from 'components/generic/FormInputGroup'
import { useTranslation } from 'react-i18next'
import RadioFieldWTN from 'components/generic/RadioFieldWTN'
import { selectOwnProfile, selectOwnProfileError, selectOwnProfileLoading } from 'redux/selectors'

const Wrapper = ({ children }) => {
  const { t } = useTranslation('strMyAccount')
  return (
    <AccountSection
      title={t('privacyAndConsents.title')}
      subtitle={t('privacyAndConsents.subtitle')}
    >
      {children}
    </AccountSection>
  )
}

enum ProfileVisibility {
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC',
}

const PrivacyAndConsentsInner = ({ isOptedOut }: { isOptedOut: boolean }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['strMyAccount', 'strCommonUI'])
  const updatingProfile = useSelector((state: AppState) => state.profile.updatingOwnProfile)

  return (
    <>
      <h3>{t('privacyAndConsents.bodyTitle')}</h3>
      <Formik
        enableReinitialize
        initialValues={{
          [FormikNames.PROFILE_VISIBILITY]: isOptedOut
            ? ProfileVisibility.PRIVATE
            : ProfileVisibility.PUBLIC,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values, actions) => {
          const { profileVisibility } = values
          dispatch(
            updateOwnProfile({
              optedOutOfPublicDataConsent: profileVisibility === ProfileVisibility.PRIVATE,
            })
          )
        }}
      >
        {({ values }) => (
          <Form className={styles.form}>
            <FormInputGroup
              label={
                <p
                  id={FormikNames.PROFILE_VISIBILITY}
                  data-formik-error={FormikNames.PROFILE_VISIBILITY}
                  className={styles.formLabel}
                >
                  {t('privacyAndConsents.form.profileVisibility.label')}
                </p>
              }
              formikName={FormikNames.PROFILE_VISIBILITY}
              classWrapper={styles.inputGroup}
            >
              <div role='group' aria-labelledby={FormikNames.PROFILE_VISIBILITY}>
                <RadioFieldWTN
                  name={FormikNames.PROFILE_VISIBILITY}
                  value={ProfileVisibility.PUBLIC}
                  text={t('privacyAndConsents.form.profileVisibility.optionPublic')}
                  className={styles.radio}
                />
                <RadioFieldWTN
                  name={FormikNames.PROFILE_VISIBILITY}
                  value={ProfileVisibility.PRIVATE}
                  text={t('privacyAndConsents.form.profileVisibility.optionPrivate')}
                  className={styles.radio}
                />
              </div>
            </FormInputGroup>
            {values[FormikNames.PROFILE_VISIBILITY] === ProfileVisibility.PRIVATE && (
              <InfoBox boxColor='yellow' iconType='alert' className={styles.alert}>
                <p>{t('privacyAndConsents.form.profileVisibility.alert')}</p>
              </InfoBox>
            )}
            <BtnPrimary
              size='medium'
              className={styles.btn}
              fullWidthAtMobile
              type='submit'
              isDisabled={updatingProfile}
            >
              {t('button.update', { ns: 'strCommonUI' })}
            </BtnPrimary>
          </Form>
        )}
      </Formik>
    </>
  )
}

const PrivacyAndConsents = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation('strMyAccount')
  const ownProfile = useSelector(selectOwnProfile)
  const loadingProfile = useSelector(selectOwnProfileLoading)
  const errorProfile = useSelector(selectOwnProfileError)

  useEffect(() => {
    if (!ownProfile && !loadingProfile) dispatch(getOwnProfile())
  }, [dispatch, ownProfile, loadingProfile])

  if (errorProfile)
    return (
      <Wrapper>
        <InfoBox textOnly iconColor='red' boxColor='red'>
          {t('infoError')}
        </InfoBox>
      </Wrapper>
    )

  if (!ownProfile || loadingProfile)
    return (
      <Wrapper>
        <LoadingSpinnerWithContainer />
      </Wrapper>
    )

  return (
    <Wrapper>
      <PrivacyAndConsentsInner isOptedOut={ownProfile.optedOutOfPublicDataConsent as boolean} />
    </Wrapper>
  )
}

export default PrivacyAndConsents
